import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Minus,
  Rows2,
  Rows3,
  Search,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn.js';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../shadcn/index.js';

import { DirectionalIcon } from './DirectionalIcon.js';

export type DataTableAlign = 'start' | 'end' | 'center';

export interface DataTableColumn<T> {
  /** Stable identifier — used as the React key and the sort token. */
  id: string;
  /** Header label (already translated by the caller). */
  header: ReactNode;
  /** Cell renderer for a row. `rowNumber` is the 1-based position in the current
   *  sorted+paginated view (for an auto "#" / serial column). */
  cell: (row: T, rowNumber: number) => ReactNode;
  /**
   * Provide to make the column sortable. The returned value drives the
   * comparison: numbers sort numerically, everything else sorts as a
   * locale-aware string. Return null/undefined to push the row last.
   */
  sortValue?: (row: T) => string | number | boolean | null | undefined;
  /**
   * Text contributed to the global search filter for this column. Columns
   * without a searchValue are ignored by search (e.g. an actions column).
   */
  searchValue?: (row: T) => string | null | undefined;
  /** Horizontal alignment of header + cells. Defaults to 'start'. */
  align?: DataTableAlign;
  /** Extra classes for the <th>. */
  headClassName?: string;
  /** Extra classes for every <td> in this column. */
  cellClassName?: string;
}

export interface DataTableFooterRow {
  key: string;
  /** Column id → what goes under that column. */
  cells: Partial<Record<string, ReactNode>>;
  className?: string;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  /** Stable key per row. */
  rowKey: (row: T) => string;
  /**
   * Show the search box. Defaults to true when at least one column exposes a
   * searchValue; pass false to force-hide it.
   */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Show the page-size selector + pagination footer. Defaults to true. */
  paginated?: boolean;
  /** Initial rows per page. Defaults to 10. */
  pageSize?: number;
  pageSizeOptions?: number[];
  /** Initial sort column + direction (e.g. newest-first). Defaults to unsorted. */
  initialSort?: { id: string; dir: 'asc' | 'desc' } | null;
  /**
   * CONTROLLED SORT — the caller owns which column is sorted.
   *
   * Two reasons to take it: the rows are paged by the server (see
   * `externallySorted`), or the page wants to REMEMBER the sort — an operator
   * who sorted by quantity, opened a product and came back should find it still
   * sorted that way. Ownership of the state and ownership of the sorting are
   * separate questions, which is why they are separate props.
   */
  sort?: { id: string; dir: 'asc' | 'desc' } | null;
  onSortChange?: (next: { id: string; dir: 'asc' | 'desc' } | null) => void;
  /**
   * `data` ARRIVES IN ORDER — do not sort it here.
   *
   * For a server-paged table only. Sorting one page of a paged list is a lie:
   * the header claims to order the whole list and actually shuffles the slice
   * in front of you, so a stock page showing 250 of 4,831 rows "sorted by
   * quantity" reads as if the catalogue held only what that page can see. A
   * table holding ALL its rows leaves this off and keeps sorting locally.
   */
  externallySorted?: boolean;
  /**
   * Extra controls rendered in the toolbar, to the start of the search box. A
   * function receives the rows exactly as the table shows them — searched and
   * sorted, every page — plus the search text, for an export or a print that
   * must match the screen.
   */
  toolbar?: ReactNode | ((view: { rows: T[]; query: string }) => ReactNode);
  /** Per-row className (e.g. to dim inactive rows). */
  rowClassName?: (row: T) => string | undefined;
  /** Click handler for a row (makes the row a button-like target). */
  onRowClick?: (row: T) => void;
  /** Adds a leading checkbox column + a floating bulk-action bar. */
  selectable?: boolean;
  /**
   * Rendered in the floating bar while rows are selected. Receives the selected
   * rows and a `clear` callback (call after a successful bulk op).
   */
  bulkActions?: (rows: T[], clear: () => void) => ReactNode;
  /**
   * Summary rows under the body — totals, subtotals. Cells are keyed by COLUMN
   * ID, so they stay under their column whichever columns the user has hidden;
   * a column with no entry gets an empty cell. The rows are given, never
   * computed here: a table that holds one page of a server-paged list cannot
   * know the total, only the caller can.
   */
  footerRows?: DataTableFooterRow[];
}

type Density = 'comfortable' | 'compact';
const DENSITY_KEY = 'erp-table-density';
function loadDensity(): Density {
  try {
    return window.localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable';
  } catch {
    return 'comfortable';
  }
}

type SortState = { id: string; dir: 'asc' | 'desc' } | null;

const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

function alignClasses(align: DataTableAlign | undefined): string {
  if (align === 'end') return 'text-end';
  if (align === 'center') return 'text-center';
  return 'text-start';
}

function justifyClasses(align: DataTableAlign | undefined): string {
  if (align === 'end') return 'justify-end';
  if (align === 'center') return 'justify-center';
  return 'justify-start';
}

/** Window of page numbers around the current page, with ellipsis sentinels. */
function pageWindow(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('…');
  for (let p = start; p <= end; p += 1) pages.push(p);
  if (end < total - 1) pages.push('…');
  pages.push(total);
  return pages;
}

/**
 * Generic, DataTables-style table: sortable column headers, client-side
 * global search, a page-size selector, and a "showing X to Y of Z" footer
 * with pagination. RTL- and i18n-aware. Operates entirely on the array it is
 * handed — wrap it in a <Card className="overflow-hidden p-0"> on the page.
 */
export function DataTable<T>({
  columns,
  data,
  rowKey,
  searchable,
  searchPlaceholder,
  paginated = true,
  pageSize: initialPageSize = 10,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  toolbar,
  rowClassName,
  onRowClick,
  selectable,
  bulkActions,
  initialSort = null,
  sort: sortProp,
  onSortChange,
  externallySorted = false,
  footerRows,
}: DataTableProps<T>): JSX.Element {
  const { t, i18n } = useTranslation();
  const [innerSort, setInnerSort] = useState<SortState>(initialSort);
  // Controlled the moment a caller offers to handle the click itself.
  const controlled = onSortChange !== undefined;
  const sort = controlled ? (sortProp ?? null) : innerSort;
  const [query, setQuery] = useState('');
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [page, setPage] = useState(0);
  const [density, setDensity] = useState<Density>(loadDensity);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    try { window.localStorage.setItem(DENSITY_KEY, density); } catch { /* ignore */ }
  }, [density]);

  const hasSearchable = columns.some((c) => c.searchValue);
  const showSearch = searchable ?? hasSearchable;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    const searchCols = columns.filter((c) => c.searchValue);
    return data.filter((row) =>
      searchCols.some((c) => (c.searchValue!(row) ?? '').toLowerCase().includes(q)),
    );
  }, [data, query, columns]);

  const sorted = useMemo(() => {
    // Already ordered by whoever produced it — see externallySorted.
    if (externallySorted) return filtered;
    if (!sort) return filtered;
    const col = columns.find((c) => c.id === sort.id);
    if (!col?.sortValue) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    // Decorate-sort-undecorate keeps the comparison stable and avoids
    // recomputing sortValue O(n log n) times.
    const decorated = filtered.map((row, index) => ({ row, index, key: col.sortValue!(row) }));
    decorated.sort((a, b) => {
      const av = a.key;
      const bv = b.key;
      const aEmpty = av === null || av === undefined || av === '';
      const bEmpty = bv === null || bv === undefined || bv === '';
      if (aEmpty && bEmpty) return a.index - b.index;
      if (aEmpty) return 1; // empties always last, regardless of direction
      if (bEmpty) return -1;
      let cmp: number;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else {
        cmp = String(av).localeCompare(String(bv), i18n.language, {
          numeric: true,
          sensitivity: 'base',
        });
      }
      return cmp !== 0 ? cmp * dir : a.index - b.index;
    });
    return decorated.map((d) => d.row);
  }, [filtered, sort, columns, i18n.language, externallySorted]);

  const total = sorted.length;
  const pageCount = paginated ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = paginated
    ? sorted.slice(currentPage * pageSize, currentPage * pageSize + pageSize)
    : sorted;

  const from = total === 0 ? 0 : currentPage * pageSize + 1;
  const to = paginated ? Math.min(total, (currentPage + 1) * pageSize) : total;

  // Selection (across the whole filtered set, not just the page).
  const allKeys = useMemo(() => sorted.map(rowKey), [sorted, rowKey]);
  const selectedRows = useMemo(() => data.filter((r) => selected.has(rowKey(r))), [data, selected, rowKey]);
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selected.has(k));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = (): void => setSelected(() => (allSelected ? new Set() : new Set(allKeys)));
  const toggleRow = (k: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  const clearSelection = (): void => setSelected(new Set());
  const colCount = columns.length + (selectable ? 1 : 0);

  const nextSort = (prev: SortState, id: string): SortState => {
    if (!prev || prev.id !== id) return { id, dir: 'asc' };
    if (prev.dir === 'asc') return { id, dir: 'desc' };
    return null; // third click clears the sort
  };

  const toggleSort = (col: DataTableColumn<T>): void => {
    if (!col.sortValue) return;
    if (controlled) { onSortChange(nextSort(sort, col.id)); return; }
    setInnerSort((prev) => nextSort(prev, col.id));
  };

  const onQueryChange = (value: string): void => {
    setQuery(value);
    setPage(0);
  };

  const showToolbar = showSearch || paginated || toolbar;

  return (
    <div className="flex flex-col">
      {showToolbar && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3">
          <div className="flex items-center gap-2">
            {paginated && (
              <label className="flex items-center gap-2 text-sm text-fg-muted">
                <span>{t('dataTable.perPage')}</span>
                <select
                  className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(0);
                  }}
                  aria-label={t('dataTable.perPage')}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span>{t('dataTable.entries')}</span>
              </label>
            )}
            {typeof toolbar === 'function' ? toolbar({ rows: sorted, query: query.trim() }) : toolbar}
            <button
              type="button"
              onClick={() => setDensity((d) => (d === 'compact' ? 'comfortable' : 'compact'))}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-bg px-2.5 text-sm text-fg-muted transition-colors hover:bg-bg-subtle hover:text-fg"
              title={t('dataTable.density', { defaultValue: 'Row density' })}
              aria-label={t('dataTable.density', { defaultValue: 'Row density' })}
            >
              {density === 'compact'
                ? <Rows3 className="h-4 w-4" aria-hidden />
                : <Rows2 className="h-4 w-4" aria-hidden />}
            </button>
          </div>
          {showSearch && (
            <div className="relative w-full sm:w-64">
              <Search
                className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-muted"
                aria-hidden
              />
              <input
                type="search"
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                placeholder={searchPlaceholder ?? t('dataTable.search')}
                aria-label={searchPlaceholder ?? t('dataTable.search')}
                className="block h-9 w-full rounded-md border border-border bg-bg ps-9 pe-3 text-sm text-fg outline-none transition placeholder:text-fg-muted focus:border-brand focus:ring-2 focus:ring-brand/20"
              />
            </div>
          )}
        </div>
      )}

      <Table className={density === 'compact' ? 'erp-dense' : undefined}>
        <TableHeader>
          <TableRow>
            {selectable && (
              <TableHead className="w-10">
                <button
                  type="button"
                  onClick={toggleAll}
                  aria-label={t('dataTable.selectAll', { defaultValue: 'Select all' })}
                  className={cn(
                    'grid h-4 w-4 place-items-center rounded border transition-colors',
                    allSelected || someSelected ? 'border-brand bg-brand text-brand-contrast' : 'border-border bg-bg hover:border-brand',
                  )}
                >
                  {allSelected ? <Check className="h-3 w-3" aria-hidden /> : someSelected ? <Minus className="h-3 w-3" aria-hidden /> : null}
                </button>
              </TableHead>
            )}
            {columns.map((col) => {
              const active = sort?.id === col.id;
              return (
                <TableHead key={col.id} className={cn(alignClasses(col.align), col.headClassName)}>
                  {col.sortValue ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col)}
                      className={cn(
                        'group inline-flex w-full items-center gap-1.5 uppercase tracking-[0.06em] transition-colors hover:text-fg',
                        active && 'text-fg',
                        justifyClasses(col.align),
                      )}
                      aria-label={
                        typeof col.header === 'string' ? col.header : undefined
                      }
                    >
                      <span>{col.header}</span>
                      {active ? (
                        sort?.dir === 'asc' ? (
                          <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                        )
                      ) : (
                        <ChevronsUpDown
                          className="h-3.5 w-3.5 text-fg-subtle/50 group-hover:text-fg-subtle"
                          aria-hidden
                        />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {pageRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={colCount}
                className="py-10 text-center text-sm text-fg-muted"
              >
                {t('dataTable.noResults')}
              </TableCell>
            </TableRow>
          ) : (
            pageRows.map((row, i) => {
              const key = rowKey(row);
              const isSel = selected.has(key);
              const rowNumber = from + i;
              return (
              <TableRow
                key={key}
                data-state={isSel ? 'selected' : undefined}
                className={cn(
                  'transition-colors',
                  rowClassName?.(row),
                  onRowClick && 'cursor-pointer hover:bg-bg-subtle active:bg-bg-muted',
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {selectable && (
                  <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => toggleRow(key)}
                      aria-label={t('dataTable.selectRow', { defaultValue: 'Select row' })}
                      className={cn(
                        'grid h-4 w-4 place-items-center rounded border transition-colors',
                        isSel ? 'border-brand bg-brand text-brand-contrast' : 'border-border bg-bg hover:border-brand',
                      )}
                    >
                      {isSel ? <Check className="h-3 w-3" aria-hidden /> : null}
                    </button>
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    className={cn(alignClasses(col.align), col.cellClassName)}
                  >
                    {col.cell(row, rowNumber)}
                  </TableCell>
                ))}
              </TableRow>
              );
            })
          )}
        </TableBody>
        {footerRows && footerRows.length > 0 && pageRows.length > 0 && (
          <TableFooter>
            {footerRows.map((fr) => (
              <TableRow key={fr.key} className={cn('hover:bg-transparent', fr.className)}>
                {selectable && <TableCell className="w-10" />}
                {columns.map((col) => (
                  <TableCell key={col.id} className={cn(alignClasses(col.align), col.cellClassName)}>
                    {fr.cells[col.id] ?? null}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableFooter>
        )}
      </Table>

      {paginated && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-3 text-sm text-fg-muted">
          <span>{t('dataTable.showing', { from, to, total })}</span>
          {pageCount > 1 && (
            <nav className="flex items-center gap-1" aria-label={t('dataTable.pagination')}>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-bg px-2.5 text-fg transition-colors hover:bg-bg-subtle disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={t('dataTable.previous')}
              >
                <DirectionalIcon>
                  <ChevronLeft className="h-4 w-4" />
                </DirectionalIcon>
              </button>
              {pageWindow(currentPage + 1, pageCount).map((p, i) =>
                p === '…' ? (
                  <span key={`gap-${i}`} className="px-1 text-fg-subtle">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p - 1)}
                    aria-current={p === currentPage + 1 ? 'page' : undefined}
                    className={cn(
                      'inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 transition-colors',
                      p === currentPage + 1
                        ? 'border-brand bg-brand text-brand-contrast'
                        : 'border-border bg-bg text-fg hover:bg-bg-subtle',
                    )}
                  >
                    {p}
                  </button>
                ),
              )}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={currentPage >= pageCount - 1}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-bg px-2.5 text-fg transition-colors hover:bg-bg-subtle disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={t('dataTable.next')}
              >
                <DirectionalIcon>
                  <ChevronRight className="h-4 w-4" />
                </DirectionalIcon>
              </button>
            </nav>
          )}
        </div>
      )}

      {selectable && bulkActions && selected.size > 0 && (
        <div className="pointer-events-none sticky bottom-4 z-20 mt-2 flex justify-center px-3">
          <div className="glass-panel pointer-events-auto flex items-center gap-3 rounded-full border px-4 py-2 shadow-lg">
            <span className="text-sm font-semibold text-fg">
              {t('dataTable.selectedCount', { defaultValue: '{{count}} selected', count: selected.size })}
            </span>
            <span className="h-4 w-px bg-border" aria-hidden />
            {bulkActions(selectedRows, clearSelection)}
            <button
              type="button"
              onClick={clearSelection}
              aria-label={t('dataTable.clearSelection', { defaultValue: 'Clear selection' })}
              className="grid h-6 w-6 place-items-center rounded-full text-fg-muted transition-colors hover:bg-bg-subtle hover:text-fg"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
